/* eslint-disable react-hooks/exhaustive-deps */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import LoadingContent from 'components/LoadingContent';
import { checkEnableParseJSON, reorder } from 'components/helper/helper';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { debounce } from 'lodash';
import React from 'react';
import TopProcessBar from 'screen/selectCollege/TopProcessBar';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { Container, Form, Spinner, Button } from 'react-bootstrap';
import { getCLEPTest, updateCLEPTest } from 'app/testingSlice/testingSlice';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CLEPConfig from './CLEPConfig';
import Grid from '@mui/joy/Grid';
import websocketService from 'services/websocketService';
import { PAGE_NAME } from 'constants/common';
import pageStatusService from 'services/pageStatusService';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';

type Props = {};

type FormItem = {
  CLEPExam: string;
  is_taken_exam: string;
  plan_taking_on: string;
  date_taken: string;
  score: string;
  is_not_received: boolean;
};

const formItem: FormItem = {
  CLEPExam: '',
  is_taken_exam: '',
  plan_taking_on: '',
  date_taken: '',
  score: '',
  is_not_received: false,
};

const initialValues: { csu_info: FormItem[] } = { csu_info: [formItem] };

const createValidationSchema = () => {
  return Yup.object().shape({
    csu_info: Yup.array().of(
      Yup.object().shape({
        date_taken: Yup.string().nullable()
          .when('is_taken_exam', {
            is: 'true',
            then: Yup.string().nullable().required('Date is required')
                .test(
                  'is-valid-date',
                  'Invalid date',
                  function (value: any) {
                    if (!value) {
                      return false;
                    }

                    let year = moment(value).format('yyyy');
                    if (Number(year) < 2000) return false;

                    return moment(value).isValid();
                  }
                ),
          }),
        plan_taking_on: Yup.string().nullable()
          .when('is_taken_exam', {
            is: 'false',
            then: Yup.string().nullable().required('Date is required')
                .test(
                  'is-valid-date',
                  'Invalid date',
                  function (value: any) {
                    if (!value) {
                      return false;
                    }
              
                    let year = moment(value).format('yyyy');
                    if (Number(year) < new Date().getFullYear()) return false;

                    return moment(value).isValid();
                  }
                ),
          }),
      })
    )
  });
};

const CLEP = (props: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);
  const testing = useAppSelector((store) => store.testing);
  const dispatch = useAppDispatch();
  const history = useHistory();  
  let studentId = getUserId();
  let token = getAccessToken();
  const [reviewedAndComplete, setReviewedAndComplete] = React.useState(false);
  const onReviewdAndComplete = () => { 
    const newValue = !reviewedAndComplete;
    setReviewedAndComplete(newValue);
    websocketService.finishReviewAndComplete(PAGE_NAME.CLEP_TEST, newValue);
  }
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createValidationSchema(),
    onSubmit: async (values) => {
      const newValues = {
        student_id: studentId,
        csu_info: JSON.stringify(values.csu_info),
      };
      await dispatch(updateCLEPTest(newValues));
    },
  });

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const handleContinue = () => {
    formik.handleSubmit();
    history.push(routers.activities.path);
  };

  const getDetail = async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      setLoading(true);
      const pageStatus: {
        student_completed: boolean;
      } = await pageStatusService.getPageStatusByPageName(studentId, PAGE_NAME.CLEP_TEST);
      if(pageStatus) {
        setReviewedAndComplete(pageStatus.student_completed);
      }
      let res = await dispatch(getCLEPTest(studentId));
      const dataLoaded: any = res.payload;
      if (dataLoaded) {
        let csuInformation = checkEnableParseJSON(dataLoaded.csu_info);
        const values: any = {
          csu_info: Array.isArray(csuInformation) ? csuInformation : [],
        };
        formik.setValues(values);
      }
      setLoading(false);
      formik.setStatus('idle');        
    }
    else {
      handleLogout();
    } 
  };

  React.useEffect(() => {
    getDetail();
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAdd = () => {
    if (formik.values.csu_info.length > 9) return;
    formik.setFieldValue('csu_info', [...formik.values.csu_info, formItem]);
  };

  const [forceRenderKey, setForceRenderKey] = React.useState(0);

  const onDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }
    const dataList = reorder(formik.values.csu_info, result.source.index, result.destination.index);
    formik.setFieldValue('csu_info', [...dataList]);

    setForceRenderKey(forceRenderKey + 1);
  };

  const setData = (data: FormItem[]) => {
    formik.setFieldValue('csu_info', [...data]);
  };
  let data = formik.values.csu_info;
  // Auto save function ==========
  const notInitialRender = React.useRef(false);
  React.useEffect(() => {
    debounceSaving();
  }, [formik.values]);
  const debounceSaving = React.useCallback(
    debounce(() => {
      if (notInitialRender.current) {
        if (!loading) formik.handleSubmit();
      } else {
        notInitialRender.current = true;
      }
    }, 1500),
    []
  );
  // Auto save function ==========
  return loading ? (
    <LoadingContent />
  ) : (
    <>
      <TopProcessBar
        stepProcess={[
          { percentOnProcess: 30, title: 'CLEP Exams', specialId: 'CLEP_Exams'},
          { percentOnProcess: 60, title: 'Test Dates', specialId: 'Test_Dates'},
          { percentOnProcess: 90, title: 'CLEP Scores', specialId: 'CLEP_Scores'}, 
        ]}
        title="Standardized Testing"
        process={1}
        icon={<CalculateOutlinedIcon />}
        isMobile={isMobile}
      />

      <Container 
        className={`main-container gm-basic-info-page ${isMobile ? 'p-mobile-2' : ''}`}
        style={{
          paddingLeft: isMobile ? '12px' : undefined,
          paddingRight: isMobile ? '12px' : undefined,
          paddingTop: isMobile ? '120px' : undefined,
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <div className={`gm-section-card ${isMobile ? 'p-mobile-2' : 'px-4 py-4'}`}>
          <Form
            style={{ 
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%'
            }}
            onSubmit={formik.handleSubmit}
            className="gm-form-grid"
          >
            <div id="CLEP_Exams" className={`gm-section-title ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600 }}>
                College Level Examination Program
              </span>
            </div>
            <Grid container spacing={isMobile ? 1 : 2} className="gm-form-fields">
              <Grid xs={12}>
                <DragDropContext onDragEnd={onDragEnd} key={forceRenderKey}>
                  <Droppable droppableId="list">
                    {(provided) => (
                      <div className='w-100 bg-white py-2 box-collapse rounded-3' ref={provided.innerRef} {...provided.droppableProps}>
                        {formik.values.csu_info.map((item, index) => (
                          <div key={index + '-' + formik.values.csu_info.length}>
                            <Draggable
                              draggableId={index + '-' + formik.values.csu_info.length}
                              index={index}
                              isDragDisabled={isMobile}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...(!isMobile ? provided.dragHandleProps : {})}
                                >
                                  <div
                                    key={index + '-' + formik.values.csu_info.length}
                                    className="position-relative"
                                  >
                                    <CLEPConfig formik={formik} key={index} index={index} isMobile={isMobile} disabled={reviewedAndComplete} />
                                    <RemoveCircleOutlineIcon
                                      className="position-absolute text-field-light pointer"
                                      style={{  
                                        opacity: reviewedAndComplete ? 0.5 : 1,
                                        cursor: reviewedAndComplete ? 'not-allowed' : 'pointer',
                                        right: isMobile ? '-12px' : '-24px', 
                                        top: isMobile ? '8px' : '12px',
                                        fontSize: isMobile ? '20px' : '24px'
                                      }}
                                      onClick={() => {
                                        if (reviewedAndComplete) return;
                                        const newACTData = [...data];
                                        newACTData.splice(index, 1);
                                        setData(newACTData);
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          </div>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Grid>
              <Grid xs={12}>
                <div className="my-3 w-100 d-flex flex-column align-items-center">
                  <Button variant="primary" className="p-2 px-5" onClick={handleAdd} disabled={reviewedAndComplete}>
                    Add CLEP Test
                  </Button>
                </div>
              </Grid>
            </Grid>
            <div className={`${isMobile ? 'my-2' : 'my-3'} d-flex ${isMobile ? 'flex-column' : 'justify-content-between'} border-top ${isMobile ? 'pt-2' : 'pt-3'}`}>
              <div className={`${isMobile ? 'w-100 mb-3' : ''} d-flex justify-content-start`}>
                <div className="d-flex flex-column align-items-start">
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input
                      type="checkbox"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #6c757d',
                        borderRadius: '2px',
                      }}
                      checked={reviewedAndComplete}
                      onClick={onReviewdAndComplete}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? '14px' : '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Reviewed & Complete!
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={`${isMobile ? 'w-100' : 'col col-md-6'} text-center ${isMobile ? '' : 'text-md-end'}`}>
                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'flex-row'} justify-content-center`}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5 mx-2'} gm-btn-save`}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {testing.status === 'loading' ? (
                      <Spinner size="sm" animation="border" variant="light" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button 
                    variant="primary" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5'} gm-btn-continue`}
                    onClick={() => handleContinue()}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Container>
    </>
  );
};

export default CLEP;
